/** Public list pages for events, places, activities. */
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, MapPin, Store } from 'lucide-react';
import {
	fetchPublicEvents, fetchPublicPlaces, fetchPublicActivities,
} from '@/services/api/public';
import { Container, ItemCard, PageTitle, Empty, fmtDateTime } from '@/pages/public/_shared';
import { PUBLIC_IDS } from '@/constants/testIds';

const ts = (v) => (v ? new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(v) ? v : `${v}Z`).getTime() : 0);

function EventSection({ title, items, testId, empty, past }) {
	return (
		<section data-testid={testId} className={past ? 'mt-12 opacity-60' : ''}>
			<h2 className="mb-4 text-base font-bold text-slate-200 md:text-lg">{title}</h2>
			{items.length === 0 ? <Empty>{empty}</Empty> : (
				<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
					{items.map((e, i) => (
						<ItemCard key={e.id} index={i} to={`/eventi/${e.slug}`} title={e.title} tag={e.category}
							cover={e.cover_url} coverIcon={CalendarDays} meta={fmtDateTime(e.start_at)} testId={`event-card-${e.slug}`} titleLines={2} />
					))}
				</div>
			)}
		</section>
	);
}

export function PublicEvents() {
	const { data = [] } = useQuery({ queryKey: ['pub-events'], queryFn: fetchPublicEvents });
	const now = Date.now();
	const isPast = (e) => ts(e.end_at || e.start_at) < now;
	const active = data.filter((e) => !isPast(e)).sort((a, b) => ts(a.start_at) - ts(b.start_at));
	const past = data.filter(isPast).sort((a, b) => ts(b.start_at) - ts(a.start_at));
	return (
		<Container>
			<PageTitle sub="Eventi pubblicati">Eventi</PageTitle>
			{data.length > 0 && (
				<div data-testid={PUBLIC_IDS.eventsList}>
					<EventSection title="In corso e in programma" items={active} testId="events-upcoming-section" empty="Nessun evento in programma." />
					{past.length > 0 && <EventSection title="Eventi passati" items={past} testId="events-past-section" past />}
				</div>
			)}
			{data.length === 0 && <Empty>Nessun evento pubblicato.</Empty>}
		</Container>
	);
}

export function PublicPlaces() {
	const { data = [] } = useQuery({ queryKey: ['pub-places'], queryFn: fetchPublicPlaces });
	return (
		<Container>
			<PageTitle sub="Luoghi del territorio">Luoghi</PageTitle>
			<div data-testid={PUBLIC_IDS.placesList} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
				{data.map((p, i) => (
					<ItemCard key={p.id} index={i} to={`/luoghi/${p.slug}`} title={p.name} tag={p.category}
						cover={p.cover_url} coverIcon={MapPin} meta={p.address} testId={`place-card-${p.slug}`} />
				))}
			</div>
			{data.length === 0 && <Empty>Nessun luogo pubblicato.</Empty>}
		</Container>
	);
}

export function PublicActivities() {
	const { data = [] } = useQuery({ queryKey: ['pub-activities'], queryFn: fetchPublicActivities });
	return (
		<Container>
			<PageTitle sub="Attività locali">Attività</PageTitle>
			<div data-testid={PUBLIC_IDS.activitiesList} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
				{data.map((a, i) => (
					<ItemCard key={a.id} index={i} to={`/attivita/${a.slug}`} title={a.title} tag={a.category}
						cover={a.cover_url} coverIcon={Store} meta={a.description} metaLines={2} testId={`activity-card-${a.slug}`} />
				))}
			</div>
			{data.length === 0 && <Empty>Nessuna attività pubblicata.</Empty>}
		</Container>
	);
}
